/**
 * Module 6D Phase 6 — Admin shell sub-pages.
 *
 * Studios list, studio detail (with suspend/restore/grant/impersonate
 * actions, all with mandatory reason fields), and the Bucket-B
 * credential-stuffing security view. Every action surfaces the reason
 * field at the modal level — server enforces ≥20 chars too.
 *
 * Impersonation flow: opens the Phase-3 launch URL in a new tab; we
 * DO NOT re-implement the token plumbing.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi } from "@/lib/admin_auth";
import { ShieldCheck, ArrowLeft, AlertOctagon, Eye, Sparkles, RotateCcw, Flag } from "lucide-react";

const REASON_MIN = 20;
const FRONTEND_ORIGIN = window.location.origin;


function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function relTime(s) {
  if (!s) return "—";
  const ms = Date.now() - Date.parse(s);
  if (Number.isNaN(ms)) return "—";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}


function PlanStatusChip({ studio }) {
  if (studio.plan_status === "suspended") {
    return (
      <span
        data-testid="status-chip-suspended"
        className="inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase"
        style={{ backgroundColor: "#7c2d12", color: "#fbbf24" }}
      >
        SUSPENDED
      </span>
    );
  }
  if (studio.granted_plan && studio.granted_until && Date.parse(studio.granted_until) > Date.now()) {
    return (
      <span
        data-testid="status-chip-granted"
        className="inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase"
        style={{ backgroundColor: "#2563eb", color: "#dbeafe" }}
      >
        COMP {studio.granted_plan?.toUpperCase()}
      </span>
    );
  }
  return (
    <span
      data-testid={`status-chip-${studio.plan_status || "active"}`}
      className="inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border"
      style={{ borderColor: "#3f3f46", color: "#a1a1aa" }}
    >
      {studio.plan_status || "active"}
    </span>
  );
}


// ─── Studios list ──────────────────────────────────────────────────────
export function StudiosList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/studios?limit=100")
      .then(r => setItems(r.data.items || []))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load studios"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-zinc-500 text-sm p-6" data-testid="studios-list-loading">Loading…</div>;
  if (error) return <div className="text-red-400 text-sm p-6">{String(error)}</div>;

  return (
    <div data-testid="studios-list">
      <div className="text-zinc-200 text-2xl mb-1" style={{ fontFamily: "serif" }}>Studios</div>
      <div className="text-zinc-500 text-xs mb-6">{items.length} on platform</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="text-left py-2 pr-4">Studio</th>
            <th className="text-left py-2 pr-4">Plan</th>
            <th className="text-left py-2 pr-4">Status</th>
            <th className="text-right py-2 pr-4">Leads</th>
            <th className="text-right py-2 pr-4">Projects</th>
            <th className="text-left py-2 pr-4">Last support view</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map(s => (
            <tr
              key={s.id}
              data-testid={`studio-row-${s.id}`}
              className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
              onClick={() => navigate(`/admin/studios/${s.id}`)}
            >
              <td className="py-3 pr-4">
                <div className="text-zinc-100">{s.name}</div>
                <div className="text-[11px] text-zinc-500">{s.slug} · {s.owner_email || "—"}</div>
              </td>
              <td className="py-3 pr-4 text-zinc-300">{s.plan}</td>
              <td className="py-3 pr-4"><PlanStatusChip studio={s} /></td>
              <td className="py-3 pr-4 text-right text-zinc-300">{s.leads_count}</td>
              <td className="py-3 pr-4 text-right text-zinc-300">{s.projects_active}</td>
              <td className="py-3 pr-4 text-xs text-zinc-400" data-testid={`last-impersonation-chip-${s.id}`}>
                {s.last_impersonation ? (
                  <>
                    {s.last_impersonation.metadata?.admin_email || "support"} · {relTime(s.last_impersonation.created_at)}
                    <div className="text-[10px] text-zinc-600 truncate max-w-xs">
                      {s.last_impersonation.metadata?.reason || ""}
                    </div>
                  </>
                ) : (
                  <span className="text-zinc-700">never</span>
                )}
              </td>
              <td className="py-3 text-zinc-500 text-xs">→</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─── Reason modal (shared by all 4 actions) ────────────────────────────
function ActionReasonModal({ open, onClose, title, label, busy, onSubmit, extraField }) {
  const [reason, setReason] = useState("");
  const [extra, setExtra] = useState(extraField?.defaultValue || "");
  useEffect(() => { if (!open) { setReason(""); setExtra(extraField?.defaultValue || ""); } }, [open, extraField]);
  if (!open) return null;
  const tooShort = reason.trim().length < REASON_MIN;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      data-testid="action-reason-modal"
    >
      <div className="bg-zinc-950 border border-zinc-800 max-w-md w-full p-6">
        <div className="text-amber-400 text-[10px] tracking-widest uppercase mb-1">{title}</div>
        <div className="text-zinc-100 text-lg mb-4" style={{ fontFamily: "serif" }}>{label}</div>
        {extraField ? (
          <div className="mb-3">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">{extraField.label}</div>
            {extraField.type === "select" ? (
              <select
                value={extra} onChange={(e) => setExtra(e.target.value)}
                data-testid={`action-extra-${extraField.name}`}
                className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-2 text-sm"
              >
                {extraField.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input
                type={extraField.type || "text"} value={extra}
                onChange={(e) => setExtra(e.target.value)}
                data-testid={`action-extra-${extraField.name}`}
                className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-2 text-sm"
              />
            )}
          </div>
        ) : null}
        <div className="mb-3">
          <div className="text-[10px] uppercase text-zinc-500 mb-1">Reason (≥{REASON_MIN} chars, audited)</div>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={4}
            data-testid="action-reason-input"
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-2 text-sm"
            placeholder="Investigating ticket #4421 — customer reported…"
          />
          <div className="text-[10px] text-zinc-500 mt-1">{reason.trim().length}/{REASON_MIN}</div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose} disabled={busy}
            data-testid="action-reason-cancel"
            className="px-4 py-2 text-xs uppercase border border-zinc-800 text-zinc-400"
          >Cancel</button>
          <button
            onClick={() => onSubmit({ reason: reason.trim(), extra })}
            disabled={busy || tooShort}
            data-testid="action-reason-submit"
            className="px-4 py-2 text-xs uppercase bg-amber-500 text-zinc-950 font-semibold disabled:opacity-50"
          >{busy ? "…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}


// ─── Studio detail ─────────────────────────────────────────────────────
export function StudioDetail() {
  const { studioId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [actionModal, setActionModal] = useState(null); // null | "suspend" | "restore" | "grant" | "impersonate" | "flag_enable" | "flag_disable"
  const [pendingFlag, setPendingFlag] = useState(null); // {name, label} — set when opening a flag toggle modal
  const [flagRegistry, setFlagRegistry] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    adminApi.get(`/admin/studios/${studioId}/detail`)
      .then(r => setDetail(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load detail"));
  }, [studioId]);
  useEffect(refresh, [refresh]);
  useEffect(() => {
    adminApi.get("/admin/studios/feature-flags/registry")
      .then(r => setFlagRegistry(r.data.flags || []))
      .catch(() => setFlagRegistry([]));
  }, []);

  if (error) return <div className="text-red-400 text-sm p-6">{String(error)}</div>;
  if (!detail) return <div className="text-zinc-500 p-6" data-testid="studio-detail-loading">Loading…</div>;

  const studio = detail.health?.studio || {};
  const suspended = studio.plan_status === "suspended";
  const activeGrant = studio.granted_plan && studio.granted_until && Date.parse(studio.granted_until) > Date.now();

  const doAction = async ({ reason, extra }) => {
    setBusy(true);
    try {
      if (actionModal === "suspend") {
        await adminApi.post(`/admin/studios/${studioId}/suspend`, { reason });
      } else if (actionModal === "restore") {
        await adminApi.post(`/admin/studios/${studioId}/restore`);
      } else if (actionModal === "grant") {
        const [tier, months] = extra.split(":");
        await adminApi.post(`/admin/studios/${studioId}/grant`, {
          tier, duration_months: parseInt(months, 10), reason,
        });
      } else if (actionModal === "impersonate") {
        const r = await adminApi.post(`/admin/impersonate/${studioId}`, { reason });
        // Open Phase-3 launch URL in a new tab — DO NOT reimplement token flow
        const hash = new URLSearchParams({
          imp_token: r.data.token,
          studio_id: studioId,
          studio_label: studio.name || studioId,
          expires_at: r.data.expires_at,
          admin_email: localStorage.getItem("ls.admin.email") || "support",
        }).toString();
        window.open(`${FRONTEND_ORIGIN}/impersonate/launch#${hash}`, "_blank", "noopener");
      } else if (actionModal === "flag_enable" && pendingFlag) {
        await adminApi.post(
          `/admin/studios/${studioId}/feature-flags/${pendingFlag.name}/enable`,
          { reason },
        );
      } else if (actionModal === "flag_disable" && pendingFlag) {
        await adminApi.post(
          `/admin/studios/${studioId}/feature-flags/${pendingFlag.name}/disable`,
          { reason },
        );
      }
      setActionModal(null);
      setPendingFlag(null);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Action failed";
      alert(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const modalConfig = {
    suspend:     { title: "DANGER",       label: `Suspend ${studio.name}?` },
    restore:     { title: "RESTORE",      label: `Restore ${studio.name}?` },
    grant:       { title: "COMP",         label: `Grant tier to ${studio.name}`,
                   extraField: { name: "grant", label: "Tier · duration", type: "select",
                                  defaultValue: "pro:3",
                                  options: [
                                    { value: "studio:1", label: "Studio — 1 month" },
                                    { value: "studio:3", label: "Studio — 3 months" },
                                    { value: "pro:1",    label: "Pro — 1 month" },
                                    { value: "pro:3",    label: "Pro — 3 months" },
                                    { value: "pro:6",    label: "Pro — 6 months" },
                                    { value: "pro:12",   label: "Pro — 12 months" }] } },
    impersonate: { title: "READ-ONLY",    label: `View ${studio.name} as support` },
    flag_enable: { title: "EXPERIMENT",   label: `Enable "${pendingFlag?.label || pendingFlag?.name}" for ${studio.name}?` },
    flag_disable:{ title: "EXPERIMENT",   label: `Disable "${pendingFlag?.label || pendingFlag?.name}" for ${studio.name}?` },
  };

  return (
    <div data-testid="studio-detail">
      <button
        onClick={() => navigate("/admin/studios")}
        data-testid="studio-detail-back"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mb-4"
      ><ArrowLeft size={12} /> Back to studios</button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-zinc-200 text-2xl mb-1" style={{ fontFamily: "serif" }}>{studio.name}</div>
          <div className="text-[11px] text-zinc-500">{studio.slug} · {studio.id}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-zinc-300">plan: {studio.plan}</span>
            <PlanStatusChip studio={studio} />
            {activeGrant ? (
              <span className="text-[10px] text-blue-300" data-testid="studio-grant-info">
                comp lapses {fmtDate(studio.granted_until)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setActionModal("impersonate")} data-testid="action-impersonate-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase bg-amber-500 text-zinc-950 font-semibold">
            <Eye size={13} /> Impersonate
          </button>
          <button onClick={() => setActionModal("grant")} data-testid="action-grant-btn"
            disabled={suspended}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase border border-blue-500 text-blue-300 disabled:opacity-40">
            <Sparkles size={13} /> Grant
          </button>
          {suspended ? (
            <button onClick={() => setActionModal("restore")} data-testid="action-restore-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase border border-emerald-500 text-emerald-300">
              <RotateCcw size={13} /> Restore
            </button>
          ) : (
            <button onClick={() => setActionModal("suspend")} data-testid="action-suspend-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase border border-red-500 text-red-300">
              <AlertOctagon size={13} /> Suspend
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        <Metric label="Leads" value={detail.health?.leads_count} />
        <Metric label="Active projects" value={detail.health?.projects_active} />
        <Metric label="Vendors" value={detail.health?.vendors_count} />
        <Metric label="Editors" value={detail.health?.editors_count} />
      </div>

      <FeatureFlagsSection
        studio={studio}
        registry={flagRegistry}
        history={studio.feature_flag_history || []}
        onToggle={(flag, wantEnabled) => {
          setPendingFlag(flag);
          setActionModal(wantEnabled ? "flag_enable" : "flag_disable");
        }}
      />

      <div className="text-zinc-300 text-xs uppercase tracking-widest mb-3">Recent audit</div>
      <div className="text-xs" data-testid="studio-recent-audit">
        {(detail.recent_audit || []).slice(0, 15).map((row, i) => (
          <div key={i} className="py-1.5 border-b border-zinc-900 flex gap-3">
            <span className="text-zinc-500 w-32">{fmtDate(row.created_at)}</span>
            <span className="text-amber-300 w-48">{row.action}</span>
            <span className="text-zinc-300 truncate flex-1">{row.actor_label || row.actor_user_id || "—"}</span>
            <span className="text-zinc-500 truncate flex-1">{row.target_label || row.target_id || ""}</span>
          </div>
        ))}
        {(detail.recent_audit || []).length === 0 ? (
          <div className="text-zinc-600 py-2">No audit rows yet.</div>
        ) : null}
      </div>

      <ActionReasonModal
        open={!!actionModal}
        onClose={() => { setActionModal(null); setPendingFlag(null); }}
        busy={busy}
        onSubmit={doAction}
        {...(modalConfig[actionModal] || {})}
      />
    </div>
  );
}


// ─── Module 6H — Feature flags section (studio-scoped experiments) ─────
function FeatureFlagsSection({ studio, registry, history, onToggle }) {
  const enabled = new Set(studio.feature_flags || []);
  // Most-recent history entry per flag for the "last toggled" caption.
  const lastByFlag = {};
  (history || []).forEach(h => {
    const cur = lastByFlag[h.flag];
    if (!cur || (h.at && h.at > cur.at)) lastByFlag[h.flag] = h;
  });

  return (
    <div className="mb-8" data-testid="studio-feature-flags">
      <div className="flex items-center justify-between mb-3">
        <div className="text-zinc-300 text-xs uppercase tracking-widest flex items-center gap-2">
          <Flag size={12} /> Feature flags
        </div>
        <div className="text-[10px] text-zinc-600 uppercase tracking-wider">experiments · not for tier gating</div>
      </div>
      {registry.length === 0 ? (
        <div className="text-zinc-600 text-xs py-2" data-testid="feature-flags-empty">
          No registered feature flags.
        </div>
      ) : (
        <div className="border border-zinc-900">
          {registry.map(flag => {
            const isOn = enabled.has(flag.name);
            const last = lastByFlag[flag.name];
            return (
              <div
                key={flag.name}
                data-testid={`feature-flag-row-${flag.name}`}
                className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-900 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-zinc-200 text-sm truncate">{flag.label}</div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">{flag.name}</div>
                  {last ? (
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                      last {last.action}d by {last.by} · {fmtDate(last.at)}
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => onToggle(flag, !isOn)}
                  data-testid={`feature-flag-toggle-${flag.name}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase font-semibold border ${
                    isOn
                      ? "border-emerald-500 text-emerald-300 hover:bg-emerald-950"
                      : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"
                  }`}
                >
                  {isOn ? "Enabled — click to disable" : "Disabled — click to enable"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function Metric({ label, value }) {
  return (
    <div className="border border-zinc-900 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-zinc-100 text-2xl">{value ?? "—"}</div>
    </div>
  );
}


// ─── Bucket-B credential stuffing view ─────────────────────────────────
export function BucketBView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi.get("/admin/security/bucket-b?limit=200")
      .then(r => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-zinc-500 text-sm p-6" data-testid="bucket-b-loading">Loading…</div>;
  return (
    <div data-testid="bucket-b-view">
      <div className="text-zinc-200 text-2xl mb-1" style={{ fontFamily: "serif" }}>Credential stuffing — Bucket B</div>
      <div className="text-zinc-500 text-xs mb-6">Top IPs by failed-login count. Read-only.</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="text-left py-2 pr-4">IP key</th>
            <th className="text-right py-2 pr-4">Failed attempts</th>
            <th className="text-left py-2 pr-4">Locked until</th>
            <th className="text-left py-2 pr-4">Last attempt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i} className="border-t border-zinc-900" data-testid={`bucket-b-row-${i}`}>
              <td className="py-2 pr-4 text-zinc-100 font-mono text-xs">{row.key}</td>
              <td className="py-2 pr-4 text-right text-amber-300">{row.count}</td>
              <td className="py-2 pr-4 text-zinc-400 text-xs">{fmtDate(row.locked_until)}</td>
              <td className="py-2 pr-4 text-zinc-400 text-xs">{fmtDate(row.last_attempt_at)}</td>
            </tr>
          ))}
          {items.length === 0 ? (
            <tr><td colSpan={4} className="text-zinc-600 py-4 text-center">No credential-stuffing activity in window.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
